import { Link } from 'react-router-dom'
import { Shield, Mail, MapPin, Phone, Clock } from 'lucide-react'
import { navigation } from '../lib/data'
import { useState, useEffect } from 'react'

const Footer = () => {
  const currentYear = new Date().getFullYear()
  const [istTime, setIstTime] = useState('')

  useEffect(() => {
    const updateTime = () => {
      const now = new Date()
      const istTime = new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit',
        minute: '2-digit',
        weekday: 'short',
        day: 'numeric',
        month: 'short'
      }).format(now)
      setIstTime(istTime)
    }

    updateTime()
    const interval = setInterval(updateTime, 60000) // Update every minute
    return () => clearInterval(interval)
  }, [])

  return (
    <footer className="bg-dsr-card backdrop-blur-md border-t border-white/10">
      <div className="container-max px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-br from-dsr-accent to-cyan-500 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-black" />
              </div>
              <span className="text-xl font-bold text-gradient">DevSecRoot</span>
            </div>
            <p className="text-dsr-text/70 mb-6 max-w-md">
              DevSecRoot unifies software engineering and security from day zero. We design scalable systems, 
              threat-model their risks, and automate defenses—so reliability, speed, and trust share the same root. 
              Build fast. Ship safer. Sleep better.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-dsr-text/70">
                <Mail className="w-4 h-4 text-dsr-accent" />
                <a href="mailto:contact@devsecroot.com" className="hover:text-dsr-accent transition-colors">
                  contact@devsecroot.com
                </a>
              </div>
              <div className="flex items-center space-x-3 text-dsr-text/70">
                <Phone className="w-4 h-4 text-dsr-accent" />
                <a href="tel:+9173121637642" className="hover:text-dsr-accent transition-colors">
                  0731-21637642
                </a>
              </div>
              <div className="flex items-center space-x-3 text-dsr-text/70">
                <MapPin className="w-4 h-4 text-dsr-accent" />
                <span>Office Address, Bholaram Ustad Marg, Indore M.P, IN</span>
              </div>
              <div className="flex items-center space-x-3 text-dsr-text/70">
                <Clock className="w-4 h-4 text-dsr-accent" />
                <div>
                  <div>Local time (IST): {istTime}</div>
                  <div className="text-sm text-dsr-text/50">Business Hours (IST): Mon–Fri 09:30–18:30</div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold text-dsr-text mb-4">Quick Links</h3>
            <ul className="space-y-3">
              {navigation.main.slice(0, 6).map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="text-dsr-text/70 hover:text-dsr-accent transition-colors link-underline"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold text-dsr-text mb-4">Services</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/security"
                  className="text-dsr-text/70 hover:text-dsr-accent transition-colors link-underline"
                >
                  Security Services
                </Link>
              </li>
              <li>
                <Link
                  to="/development"
                  className="text-dsr-text/70 hover:text-dsr-accent transition-colors link-underline"
                >
                  Development Services
                </Link>
              </li>
              <li>
                <Link
                  to="/case-studies"
                  className="text-dsr-text/70 hover:text-dsr-accent transition-colors link-underline"
                >
                  Case Studies
                </Link>
              </li>
              <li>
                <Link
                  to="/insights"
                  className="text-dsr-text/70 hover:text-dsr-accent transition-colors link-underline"
                >
                  Insights & Blog
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-dsr-text/70 text-sm">
              © {currentYear} DevSecRoot. All rights reserved.
            </div>
            
            <div className="flex items-center space-x-6">
              {/* Legal Links */}
              <div className="flex items-center space-x-6 text-sm">
                {navigation.footer.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="text-dsr-text/70 hover:text-dsr-accent transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div className="flex justify-center mt-6">
            <div className="flex items-center space-x-6">
              {navigation.social.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-dsr-card border border-white/10 rounded-lg flex items-center justify-center text-dsr-text/70 hover:text-dsr-accent hover:border-dsr-accent/30 transition-all duration-200"
                >
                  <span className="sr-only">{item.name}</span>
                  {/* Icon would be dynamically imported based on item.icon */}
                  <div className="w-5 h-5">
                    {item.icon === 'Github' && <Shield className="w-5 h-5" />}
                    {item.icon === 'Linkedin' && <Shield className="w-5 h-5" />}
                    {item.icon === 'Twitter' && <Shield className="w-5 h-5" />}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
